import { useRouter } from 'next/router'
import { useMemo } from 'react'

import useBootstrapData from '@/hooks/useBootstrapData'
import { mapFlightCabinClassFromUrlQuery } from '@/shared-utils/flight-cabin-class-from-query'
import { isRebookFlightDetails } from '@/shared-utils/flight-rebook-helpers'

import { initialState } from '../SearchForm/utils'
import type { FlightFormStateType } from '../types'

// Hook to enable/handle Prepopulation, SEM, Rebook/Modify & initialization of flight form state
const useInitialFlightFormValues = (isRebook = false, isModify = false) => {
  const { pathname, query } = useRouter()
  const isHomepage = pathname === '/'

  const {
    isMobile,
    prePopData,
    flightRebookOrModifyDetails,
    appName,
    appVersion,
    webstats: { clientGUID: cguid },
  } = useBootstrapData()

  const { flights, flightType, cabinClass, adultCount, childrenCount, infantCount } =
    prePopData?.flights ?? {}

  const rebookCarrierName = flightRebookOrModifyDetails?.carrierName ?? ''
  const rebookCreditAmount =
    flightRebookOrModifyDetails && isRebookFlightDetails(flightRebookOrModifyDetails)
      ? flightRebookOrModifyDetails.creditAmount
      : ''

  const initialValues: FlightFormStateType = useMemo(() => {
    const base: FlightFormStateType = {
      ...initialState,
      ...(flights && { flights }),
      ...(flightType && { flightType }),
      ...(cabinClass && { cabinClass }),
      travelers: {
        adults: adultCount ?? 1,
        children: childrenCount ?? 0,
        lapInfants: infantCount ?? 0,
        childrenAges: {},
        rooms: 0,
      },
    }

    let next: FlightFormStateType =
      (isRebook || isModify) && flightRebookOrModifyDetails
        ? {
            ...base,
            cabinClass: flightRebookOrModifyDetails.cabinClass,
            travelers: {
              adults: flightRebookOrModifyDetails.passengerCount.adultCount ?? 1,
              children: flightRebookOrModifyDetails.passengerCount.childrenCount ?? 0,
              lapInfants: flightRebookOrModifyDetails.passengerCount.infantCount ?? 0,
              childrenAges: {},
              rooms: 0,
            },
          }
        : base

    if (!(isRebook || isModify)) {
      const fromQuery = mapFlightCabinClassFromUrlQuery({ value: query.cabinClass })
      if (fromQuery) {
        next = { ...next, cabinClass: fromQuery }
      }
    }

    return next
  }, [
    flights,
    flightType,
    cabinClass,
    adultCount,
    childrenCount,
    infantCount,
    isRebook,
    isModify,
    flightRebookOrModifyDetails,
    query.cabinClass,
  ])

  return {
    initialValues,
    isMobile,
    cguid,
    isHomepage,
    appName,
    appVersion,
    rebookCarrierName,
    rebookCreditAmount,
    query,
  }
}

export default useInitialFlightFormValues
